/**
 * EN-836 — recording persistence helpers.
 *
 * The web recorder must always flush MediaRecorder data on a finite, periodic cadence so
 * the in-progress recording can be snapshotted to IndexedDB and recovered after a
 * mid-recording reload/crash. Starting MediaRecorder with `undefined` (no timeslice) was
 * the root cause of the Homestead recording-loss incident: audio lived only in the
 * in-memory MediaRecorder buffer until stop, so any interruption lost it entirely.
 */

/** Snapshot cadence (ms) for the production path (CHUNK_UPLOAD off). Bounds worst-case
 * mid-recording loss to this window. Partial chunks are NOT uploaded — only persisted to
 * IndexedDB; the complete file is still assembled and uploaded once at stop. */
export const PERSIST_SNAPSHOT_MS = 15_000;

/** Cadence (ms) used when the experimental CHUNK_UPLOAD flag is on. */
export const CHUNK_UPLOAD_TIMESLICE_MS = 60_000;

/** EN-1609: a row whose owner tab doesn't answer the liveness probe is recovered only once its last
 * snapshot is at least this old. A live but background-throttled tab can miss the probe window; a
 * recording tab re-stamps snapshotAt every PERSIST_SNAPSHOT_MS and a paused one every
 * SNAPSHOT_HEARTBEAT_MS, so a row this stale has no live writer. */
export const STALE_OWNER_MS = 10 * 60_000;

/** EN-1609: how often a paused session re-stamps its snapshot's snapshotAt. A paused MediaRecorder
 * emits no data, so without this a long pause would make the row look abandoned. Well under
 * STALE_OWNER_MS so a couple of throttled or missed beats can't let it go stale. */
export const SNAPSHOT_HEARTBEAT_MS = 60_000;

/**
 * The MediaRecorder timeslice to start with. Always finite so `ondataavailable` fires
 * periodically during recording (never only at stop).
 *
 * @param chunkUploadEnabled whether the CHUNK_UPLOAD feature flag is enabled
 */
export function resolveRecorderTimeslice(chunkUploadEnabled: boolean): number {
    return chunkUploadEnabled ? CHUNK_UPLOAD_TIMESLICE_MS : PERSIST_SNAPSHOT_MS;
}

/**
 * Whether incomplete-upload recovery may run right now.
 *
 * `retryIncompleteUploads` re-uploads every incomplete IndexedDB entry. During an active recording
 * the recorder writes periodic *incomplete* snapshots (bytesUploaded=0) of the in-progress file, so
 * running recovery mid-recording would upload a partial file, transcribe it, and then upload the
 * full file again at stop — a truncated transcription plus a duplicate section/doc-gen (EN-652).
 * Recovery must therefore only run when nothing is recording or uploading. Startup is naturally
 * safe (nothing records at boot); this guard is what makes the network-restore trigger safe.
 */
export function shouldRecoverIncompleteUploads(params: { isRecording: boolean; isUploading: boolean }): boolean {
    return !params.isRecording && !params.isUploading;
}

/**
 * Cross-tab recording liveness, over BroadcastChannel.
 *
 * The per-tab recovery gate can't see a recording in *another* tab, but mid-recording snapshots
 * (bytesUploaded=0) are written to shared IndexedDB every PERSIST_SNAPSHOT_MS. So a second tab
 * running recovery could upload an in-progress recording's partial file (EN-652). We ask other
 * browsing contexts whether any is actively recording; a *live* tab answers, a *crashed* one is
 * gone and can't — so a genuinely crashed recording still recovers (no crash-recovery regression).
 */
export const RECORDING_LIVENESS_CHANNEL = "happydoc-recording-liveness";
const LIVENESS_PING = "who-is-recording";
const LIVENESS_PONG = "recording-active";

/**
 * A per-tab nonce, stamped onto every ping this tab emits. Per the BroadcastChannel spec a posted
 * message is delivered to every same-named channel object EXCEPT the sending one — including this
 * tab's *own* responder instance. Without a nonce the probe's ping would be answered by its own
 * tab's responder, so recovery would falsely see "recording elsewhere" and never run. Responders
 * ignore pings carrying their own tab's nonce, so a tab never answers itself; only genuinely
 * separate tabs (different nonce) reply.
 */
const TAB_NONCE: string =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `tab-${Math.floor(Math.random() * 1e9)}-${Math.floor(Math.random() * 1e9)}`;

/** This tab's nonce, for other cross-tab protocols that must tell their own messages from a sibling tab's. */
export function getTabNonce(): string {
    return TAB_NONCE;
}

/**
 * EN-1609: the nonce of the page this tab showed before the current load (a reload, or a crash and
 * restore), from sessionStorage, which survives reloads within one tab. Recovery treats that page's
 * rows as abandoned right away instead of waiting out STALE_OWNER_MS. The current nonce is always
 * fresh, never the stored one: "Duplicate tab" copies sessionStorage, and two live tabs must never
 * share a nonce. null on a first load or when sessionStorage is unavailable.
 */
const TAB_NONCE_STORAGE_KEY = "happydoc-tab-nonce";
const PREVIOUS_TAB_NONCE: string | null = (() => {
    let previous: string | null = null;
    try {
        previous = window.sessionStorage.getItem(TAB_NONCE_STORAGE_KEY);
    } catch {
        // Unavailable (privacy mode, sandboxed frame): no previous nonce, recovery uses staleness only.
    }
    try {
        window.sessionStorage.setItem(TAB_NONCE_STORAGE_KEY, TAB_NONCE);
    } catch {
        // Same as above; the next load just won't know this page's nonce.
    }
    return previous;
})();

/** The previous page's nonce in this tab (see PREVIOUS_TAB_NONCE), or null. */
export function getPreviousTabNonce(): string | null {
    return PREVIOUS_TAB_NONCE;
}

/**
 * EN-1609: `v: 2` marks a prober that understands the nonce PONG. Pre-EN-1609 probers send no `v` and
 * only count the bare LIVENESS_PONG string as "recording", so responders answer them with that.
 */
const LIVENESS_PING_VERSION = 2;
type LivenessPing = { kind: typeof LIVENESS_PING; nonce: string; v?: number };

/** Builds the ping payload for a given tab nonce. Exported for testing the nonce guard. */
export function buildLivenessPing(nonce: string): LivenessPing {
    return { kind: LIVENESS_PING, nonce, v: LIVENESS_PING_VERSION };
}

/**
 * Whether a responder holding `myNonce` should answer `data`. True only when `data` is a well-formed
 * ping from a *different* tab (nonce mismatch). A tab never answers its own ping — this is the guard
 * that stops the same-tab self-PONG. Pure and side-effect-free so it can be unit-tested without the
 * BroadcastChannel plumbing.
 */
export function shouldAnswerLivenessPing(data: unknown, myNonce: string): boolean {
    if (typeof data !== "object" || data === null) return false;
    const ping = data as Partial<LivenessPing>;
    return ping.kind === LIVENESS_PING && typeof ping.nonce === "string" && ping.nonce !== myNonce;
}

/**
 * EN-1609: the PONG names the answering tab, so recovery can tell *which* tabs are live and recover a
 * dead tab's rows per row instead of aborting whenever any tab records. Pre-EN-1609 builds answer with
 * the bare string LIVENESS_PONG (no nonce) — still accepted, reported as `legacy`.
 */
type LivenessPong = { kind: typeof LIVENESS_PONG; nonce: string };

/** Builds the PONG payload for a given tab nonce. */
export function buildLivenessPong(nonce: string): LivenessPong {
    return { kind: LIVENESS_PONG, nonce };
}

/** Whether `data` is a liveness PONG (i.e. some other tab reported an active recording), new or legacy. */
export function isLivenessPong(data: unknown): boolean {
    if (data === LIVENESS_PONG) return true;
    if (typeof data !== "object" || data === null) return false;
    const pong = data as Partial<LivenessPong>;
    return pong.kind === LIVENESS_PONG && typeof pong.nonce === "string";
}

/**
 * Collects the nonces of every *other* tab that reports an active recording within `timeoutMs`.
 * `legacy` is true if any answer came from a pre-EN-1609 tab, whose bare PONG carries no nonce — the
 * caller can't tell which rows that tab owns, so it must treat every row as possibly live.
 * Resolves an empty, non-legacy result when BroadcastChannel is unavailable or nobody answers.
 */
export function findLiveRecordingTabs(timeoutMs = 250): Promise<{ nonces: Set<string>; legacy: boolean }> {
    const result = { nonces: new Set<string>(), legacy: false };
    if (typeof BroadcastChannel === "undefined") return Promise.resolve(result);
    return new Promise((resolve) => {
        const channel = new BroadcastChannel(RECORDING_LIVENESS_CHANNEL);
        channel.onmessage = (event: MessageEvent) => {
            if (!isLivenessPong(event.data)) return;
            if (event.data === LIVENESS_PONG) result.legacy = true;
            else result.nonces.add((event.data as LivenessPong).nonce);
        };
        channel.postMessage(buildLivenessPing(TAB_NONCE));
        setTimeout(() => {
            channel.close();
            resolve(result);
        }, timeoutMs);
    });
}

/**
 * Resolves true if any *other* tab reports an active recording within `timeoutMs`.
 * Resolves false when the BroadcastChannel API is unavailable (feature-detected) or nobody answers.
 */
export async function isRecordingActiveElsewhere(timeoutMs = 250): Promise<boolean> {
    const live = await findLiveRecordingTabs(timeoutMs);
    return live.legacy || live.nonces.size > 0;
}

/**
 * EN-1609: asks whether the tab holding `nonce` is still open, recording or not. Recovery uses it before
 * treating the previous page's rows as abandoned: after a reload that page is gone, but "Duplicate tab"
 * copies sessionStorage, so the duplicate's "previous" nonce belongs to the still-open original tab,
 * which may be uploading those rows right now. Answered by createRecordingLivenessResponder; pre-EN-1609
 * tabs ignore the probe, and they never stored a nonce, so they can't be a duplicate's original.
 * Resolves false when BroadcastChannel is unavailable or nobody answers within `timeoutMs`.
 */
const PRESENCE_PROBE = "is-tab-open";
const PRESENCE_REPLY = "tab-open";
type PresenceMessage = { kind: typeof PRESENCE_PROBE | typeof PRESENCE_REPLY; nonce: string };

export function buildPresenceProbe(nonce: string): PresenceMessage {
    return { kind: PRESENCE_PROBE, nonce };
}

function buildPresenceReply(nonce: string): PresenceMessage {
    return { kind: PRESENCE_REPLY, nonce };
}

function isPresenceProbeFor(data: unknown, nonce: string): boolean {
    const msg = data as Partial<PresenceMessage> | null;
    return typeof msg === "object" && msg !== null && msg.kind === PRESENCE_PROBE && msg.nonce === nonce;
}

export function isTabOpen(nonce: string, timeoutMs = 250): Promise<boolean> {
    if (typeof BroadcastChannel === "undefined") return Promise.resolve(false);
    return new Promise((resolve) => {
        const channel = new BroadcastChannel(RECORDING_LIVENESS_CHANNEL);
        const finish = (open: boolean) => {
            clearTimeout(timer);
            channel.close();
            resolve(open);
        };
        channel.onmessage = (event: MessageEvent) => {
            const msg = event.data as Partial<PresenceMessage> | null;
            if (typeof msg === "object" && msg !== null && msg.kind === PRESENCE_REPLY && msg.nonce === nonce) {
                finish(true);
            }
        };
        const timer = setTimeout(() => finish(false), timeoutMs);
        channel.postMessage(buildPresenceProbe(nonce));
    });
}

/**
 * Installs a responder that answers liveness pings from *other* tabs while `isRecordingNow()` returns
 * true: with this tab's nonce to a v2 ping, with the bare legacy string to an unversioned (pre-EN-1609)
 * one. Ignores this tab's own pings via the nonce guard. Returns a cleanup function; a no-op cleanup
 * when BroadcastChannel is unavailable.
 */
export function createRecordingLivenessResponder(isRecordingNow: () => boolean): () => void {
    if (typeof BroadcastChannel === "undefined") return () => {};
    const channel = new BroadcastChannel(RECORDING_LIVENESS_CHANNEL);
    channel.onmessage = (event: MessageEvent) => {
        if (isPresenceProbeFor(event.data, TAB_NONCE)) {
            // Answered whether or not this tab records: the prober asks whether the tab is open at all.
            channel.postMessage(buildPresenceReply(TAB_NONCE));
            return;
        }
        if (shouldAnswerLivenessPing(event.data, TAB_NONCE) && isRecordingNow()) {
            const versioned = (event.data as LivenessPing).v === LIVENESS_PING_VERSION;
            channel.postMessage(versioned ? buildLivenessPong(TAB_NONCE) : LIVENESS_PONG);
        }
    };
    return () => channel.close();
}
